/**
 * Component: Widget SDK UMD auto-init entrypoint
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R02
 *
 * Entrypoint for the hosted UMD/IIFE bundle consumed via a
 * single script tag:
 *
 *     <script src="https://galen-portal.example.com/widget/v1.js"
 *             data-galen-token="..."
 *             data-galen-surface="chat"
 *             data-galen-container="galen-chat"
 *             async></script>
 *     <div id="galen-chat"></div>
 *
 * The hosted bundle inlines React so a plain HTML page with
 * no build step can embed the widget. Host callbacks
 * (onAuthRefresh, onError) are wired via named global
 * functions (e.g. ``window.onGalenAuthRefresh``) because the
 * script-tag integration has no other way to pass closures.
 *
 * Batch A scope: only the script-tag parsing and the
 * documented global-callback discovery shell ship here. The
 * actual initialisation pipeline runs through the same
 * ``initGalenWidget`` function as the NPM path; Batch B
 * fleshes out that pipeline. Until then, invoking the
 * auto-init path throws via the same stub the NPM path does.
 */

import { initGalenWidget, type InitGalenWidgetOptions } from './index';
import { mountSurface } from './surfaces/mount';

export { GALEN_WIDGET_VERSION } from './index';

// Manual API exposed on the UMD global (``window.GalenWidget``) for host
// pages that need finer control than the data-attribute auto-init flow —
// e.g., a multi-surface chooser that initialises the runtime once and
// mounts/unmounts different surfaces on user interaction. The auto-init
// path remains the documented default; these exports are an escape hatch
// for the multi-surface case. Adding the exports does not grow the UMD
// bundle: both functions were already imported above for the auto-init
// flow, so this only adds names to the export table.
export { initGalenWidget } from './index';
export type { InitGalenWidgetOptions } from './index';
export { mountSurface } from './surfaces/mount';
export type { WidgetSurfaceHandle } from './surfaces/mount';
export type { WidgetRuntime } from './core/init';

/**
 * Read the ``data-galen-*`` attributes from the currently
 * executing ``<script>`` tag. Uses ``document.currentScript``
 * when available (modern browsers) and falls back to the
 * last ``<script>`` in the DOM for older runtimes.
 *
 * Returns null when no such tag can be located, which lets
 * the caller abort cleanly rather than blow up in odd
 * embedding contexts (iframes without a script tag, etc.).
 */
function resolveHostScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) {
    return current;
  }
  const scripts = document.getElementsByTagName('script');
  return scripts.length > 0 ? (scripts[scripts.length - 1] ?? null) : null;
}

/**
 * Build the ``InitGalenWidgetOptions`` by reading
 * ``data-galen-*`` attributes off the host script tag and
 * looking up documented callback names on ``window``. The
 * callback names are pinned so host pages can declare them
 * before the script loads.
 */
function buildOptionsFromScriptTag(
  script: HTMLScriptElement,
): InitGalenWidgetOptions | null {
  const token = script.dataset.galenToken;
  const baseUrl =
    script.dataset.galenBaseUrl ?? new URL(script.src).origin;
  const containerId = script.dataset.galenContainer;
  const surface = script.dataset.galenSurface;
  // WIDGET-V1-R26: data-galen-token is optional. Omit it to run the
  // widget anonymously (the widget mints an anonymous-role session
  // token itself, provided the deployment opts in). data-galen-container
  // is still required.
  if (!containerId) {
    return null;
  }
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn(
      `[GalenWidget] data-galen-container="${containerId}" did not resolve ` +
        'to an element in the DOM; auto-init skipped.',
    );
    return null;
  }

  const w = window as unknown as {
    onGalenAuthRefresh?: () => Promise<string>;
    onGalenError?: (err: Error) => void;
  };

  return {
    baseUrl,
    ...(token ? { token } : {}),
    container,
    surface,
    onAuthRefresh: w.onGalenAuthRefresh,
    onError: w.onGalenError,
  };
}

/**
 * Auto-run on DOM ready. Invoked once per script tag. The
 * auto-initialiser is opinionated — it reads the widget's
 * own script-tag dataset and calls ``initGalenWidget``. The
 * NPM path uses the same function directly instead of going
 * through this entrypoint.
 *
 * Batch A: throws via ``initGalenWidget``'s Batch-A stub.
 * Batch B wires the pipeline to actually render.
 */
async function autoInit(): Promise<void> {
  const script = resolveHostScript();
  if (!script) {
    console.warn(
      '[GalenWidget] Could not locate the host <script> tag; ' +
        'auto-init skipped. Use the NPM path if document.currentScript is ' +
        'unavailable in your embedding context.',
    );
    return;
  }

  const options = buildOptionsFromScriptTag(script);
  if (!options) {
    console.warn(
      '[GalenWidget] data-galen-container missing on host <script> tag ' +
        '(or it does not resolve to an element); auto-init skipped. ' +
        'See the WIDGET-V1 integration guide. (data-galen-token is ' +
        'optional — omit it to run the widget anonymously where the ' +
        'deployment allows it.)',
    );
    return;
  }

  try {
    const runtime = await initGalenWidget(options);
    // The UMD auto-init path mounts the requested surface
    // automatically; the NPM path returns the runtime
    // handle and lets the caller render surfaces itself.
    // Without this, a data-galen-surface="chat" script tag
    // would initialise the widget but leave the container
    // empty after the theme has been applied.
    if (options.surface !== undefined) {
      mountSurface(runtime, options.surface, { onError: options.onError });
    }
  } catch (err) {
    if (options.onError) {
      options.onError(err instanceof Error ? err : new Error(String(err)));
    } else {
      console.error('[GalenWidget] Auto-init failed:', err);
    }
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void autoInit();
    });
  } else {
    void autoInit();
  }
}
